/**
 * Cover a short envelope by taking the same amount out of one that still
 * has money left. Two filed manual rows, one on each side.
 */

import { useMemo, useState } from 'react';
import {
  formatAmountInput,
  formatMoney,
  parseMoney,
  type BudgetItemBalance,
} from '@alohamula/domain';
import { Button, Dialog, Field, Input, MoneyInput, Select } from './ui';
import { useToast } from './ui/ToastProvider';
import { useBorrowBetweenItems } from '../api/queries';

interface BorrowDialogProps {
  recipient: BudgetItemBalance;
  donors: BudgetItemBalance[];
  date: string;
  onClose: () => void;
}

export function BorrowDialog({ recipient, donors, date, onClose }: BorrowDialogProps) {
  const shortfall = Math.max(0, Math.round(-recipient.remaining * 100) / 100);
  const ordered = useMemo(
    () => [...donors].filter((row) => row.remaining > 0.005).sort((a, b) => b.remaining - a.remaining),
    [donors]
  );
  const [fromId, setFromId] = useState(ordered[0]?.item.id ?? '');
  const donor = ordered.find((row) => row.item.id === fromId);
  const cap = donor?.remaining ?? 0;
  const [amountText, setAmountText] = useState(
    formatAmountInput(shortfall > 0 ? Math.min(shortfall, cap || shortfall) : cap)
  );
  const [note, setNote] = useState('');
  const [failure, setFailure] = useState<string | null>(null);
  const borrow = useBorrowBetweenItems();
  const toast = useToast();

  const amount = parseMoney(amountText);
  const amountError =
    amountText.trim().length === 0
      ? 'Enter how much to borrow'
      : amount == null
        ? 'Use a number, like 40.00'
        : amount > cap + 0.005
          ? `${donor?.item.name ?? 'That envelope'} only has ${formatMoney(cap)} left.`
          : undefined;

  async function save() {
    if (!donor || amount == null || amountError) return;
    setFailure(null);
    try {
      await borrow.mutateAsync({
        amount,
        fromId: donor.item.id,
        fromName: donor.item.name,
        toId: recipient.item.id,
        toName: recipient.item.name,
        date,
        note,
      });
      toast.show(`Borrowed ${formatMoney(amount)} from ${donor.item.name}`);
      onClose();
    } catch (error) {
      setFailure(error instanceof Error ? error.message : 'Could not borrow.');
    }
  }

  return (
    <Dialog
      open
      title={`Cover ${recipient.item.name}`}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            emphasis="primary"
            disabled={ordered.length === 0 || Boolean(amountError) || borrow.isPending}
            loading={borrow.isPending}
            onClick={() => void save()}
          >
            Borrow
          </Button>
        </>
      }
    >
      <p>
        Short by {formatMoney(shortfall)}. The same amount leaves the envelope you pick
        and lands here.
      </p>
      {ordered.length === 0 ? (
        <p>No envelope has money left to lend.</p>
      ) : (
        <>
          <Field label="Borrow from">
            {({ id }) => (
              <Select
                id={id}
                value={fromId}
                onChange={(event) => {
                  setFromId(event.target.value);
                  const next = ordered.find((row) => row.item.id === event.target.value);
                  if (next && amount != null && amount > next.remaining) {
                    setAmountText(formatAmountInput(Math.min(shortfall, next.remaining)));
                  }
                }}
              >
                {ordered.map((row) => (
                  <option key={row.item.id} value={row.item.id}>
                    {row.item.name} · {formatMoney(row.remaining)} left
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Amount" error={amountError}>
            {({ id, describedBy, invalid }) => (
              <MoneyInput
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
                value={amountText}
                onChange={(event) => setAmountText(event.target.value)}
              />
            )}
          </Field>
          <Field label="Note" hint="Optional. Both sides will name who borrowed from whom.">
            {({ id }) => (
              <Input
                id={id}
                value={note}
                placeholder="Month-end balance"
                onChange={(event) => setNote(event.target.value)}
              />
            )}
          </Field>
        </>
      )}
      {failure && <p className="field__error">{failure}</p>}
    </Dialog>
  );
}

export function BalanceListDialog({
  shortItems,
  donors,
  date,
  onClose,
}: {
  shortItems: BudgetItemBalance[];
  donors: BudgetItemBalance[];
  date: string;
  onClose: () => void;
}) {
  const [recipient, setRecipient] = useState<BudgetItemBalance | null>(null);

  if (recipient) {
    return (
      <BorrowDialog
        recipient={recipient}
        donors={donors.filter((row) => row.item.id !== recipient.item.id)}
        date={date}
        onClose={() => setRecipient(null)}
      />
    );
  }

  return (
    <Dialog
      open
      title="Balance short envelopes"
      onClose={onClose}
      footer={<Button onClick={onClose}>Done</Button>}
    >
      {shortItems.length === 0 ? (
        <p>Every envelope and bill has money left, or is even.</p>
      ) : (
        <ul className="tx-list">
          {shortItems.map((row) => (
            <li key={row.item.id}>
              <Button
                emphasis="ghost"
                block
                onClick={() => setRecipient(row)}
              >
                {row.item.name} · short {formatMoney(Math.abs(row.remaining))}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Dialog>
  );
}
